import { useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Wrapper } from 'components'
import { locale } from 'locale'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Input,
  Label,
  Spinner,
  Textarea,
  toast,
} from 'keep-react'
import { productService } from 'api/services/product'
import { ProductType } from 'api/types/product'
import { deltaToHtmlQuill } from 'utils/quillToHtml'
import { RouteNamesAuth } from 'routes/enum'
import { Download } from 'phosphor-react'
import { AuthContext } from 'contexts/AuthContext'
import { AppContext } from 'contexts/AppContext'
import { emailService } from 'api/services/email'

const validationSchema = Yup.object().shape({
  nif: Yup.string()
    .matches(/^[0-9]{9}$/, locale.validation.digits9)
    .required(locale.required),
  name: Yup.string().required(locale.required),
  address1: Yup.string().required(locale.required),
  postal_code: Yup.string()
    .matches(/^\d{4}-\d{3}$/, locale.validation.zip) // Ensures "XXXX-XXX" format
    .required(locale.required),
  content: Yup.string().min(10, locale.min10Chars).required(locale.required),
})

export const Product = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { currentUser, currentBroker } = useContext(AuthContext)
  const { settings, isCallingApi, setIsCallingApi } = useContext(AppContext)
  const [content, setContent] = useState<string>('')
  const [image, setImage] = useState<string | null>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [product, setProduct] = useState<ProductType | null>(null)

  const getProduct = async () => {
    if (!slug) return
    setIsLoading(true)

    try {
      const response = (await productService.getPortalProduct({
        product: slug,
      })) as ProductType
      setProduct(response)

      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      toast.error('Erro ao carregar o produto')
    }
  }

  const formik = useFormik({
    initialValues: {
      nif: '',
      name: '',
      address1: '',
      postal_code: '',
      content: '',
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema,
    onSubmit: async values => {
      setIsCallingApi(true)
      const body = {
        product: product?.product || '',
        broker: currentBroker?.BrokerName || '',
        user: currentUser?.Name || '',
        userEmail: currentUser?.Email || '',
        nif: values.nif,
        name: values.name,
        address1: values.address1,
        postal_code: values.postal_code,
        content: values.content,
      }

      let html = '<h3>Pedido de cotação</h3>'
      html += `<p>Pedido de cotação recebido de ${body.userEmail} com os seguintes dados:</p>`
      html += Object.entries(body)
        .map(([key, value]) => `<p><strong>${key}:</strong> ${value}</p>`)
        .join('')

      try {
        await emailService.sendEmail({
          html,
          emailTo: settings?.emailThatReceivesSimulationRequests || '',
          subject: 'Pedido de cotação',
        })
        toast.success('Pedido de cotação enviado com sucesso.')
      } catch (error) {
        toast.error('Erro ao enviar o pedido de cotação.')
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  useEffect(() => {
    getProduct()
  }, [slug])

  useEffect(() => {
    // Exit early if product or its files are not available
    if (!product || !product.productFiles || product.productFiles.length === 0)
      return

    const image = product.productFiles.find(file => file.label === 'Imagem')
    if (image) {
      setImage(image.uri)
    }
  }, [product])

  useEffect(() => {
    // Exit early if product or its files are not available
    if (!product || !product.productFiles || product.productFiles.length === 0)
      return

    const image = product.productFiles.find(file => file.label === 'Imagem')
    if (image) {
      setImage(image.uri)
    }
  }, [product])

  useEffect(() => {
    if (!product?.content) return
    const html = deltaToHtmlQuill(product.content)
    setContent(html)
  }, [product?.content])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !product ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Produto indisponível
            </div>
          </Card>
        ) : (
          <>
            <h1 className="text-xxxl font-medium text-primary-600">
              {product ? product?.description : 'Carregando...'}
            </h1>
            <div className="lg:py-8">
              {image && (
                <Card className="w-full max-w-none mb-4">
                  <div className="w-full h-[200px] relative overflow-hidden rounded-xl">
                    <img
                      src={image}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </Card>
              )}

              <div className="grid grid-col-1 lg:grid-cols-[3fr_1fr] gap-4 w-full">
                <Card className="w-full max-w-full p-4 md:p-6">
                  <div
                    dangerouslySetInnerHTML={{ __html: content }}
                    className="[&_h2]:text-xxl [&_h3]:text-xl [&_h4]:text-lg [&_h2]:py-4 [&_h3]:py-3 [&_h4]:py-2 [&_p]:text-body-4 [&_p]:pb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:pb-4 [&_ul]:text-body-4 [&_ol]:list-disc [&_ol]:pl-6 [&_ol]:pb-4 [&_ol]:text-body-4 [&_a]:text-primary-600 [&_a]:underline"
                  ></div>
                </Card>
                <div className="space-y-6">
                  <Card className="w-full max-w-full p-4 md:p-6">
                    <h3 className="text-lg uppercase font-light text-metal-900 pb-4 tracking-tighter">
                      Documentos
                    </h3>
                    <ul className="space-y-4">
                      {product?.productFiles &&
                      product.productFiles.length > 0 ? (
                        product.productFiles.map(file => {
                          if (file.label === 'Imagem') return null
                          return (
                            <li key={file.id} className="w-full">
                              <Button
                                color="secondary"
                                variant="softBg"
                                className="w-full justify-start"
                              >
                                <Link to={file.uri} target="_blank">
                                  <span className="gap-2 flex items-center justify-start">
                                    <Download size={20} />
                                    {file.label}
                                  </span>
                                </Link>
                              </Button>
                            </li>
                          )
                        })
                      ) : (
                        <li>Nenhum documento disponível.</li>
                      )}
                    </ul>
                  </Card>
                  {product?.hasSimulation && (
                    <Card className="w-full max-w-full p-4 md:p-6">
                      <Button
                        color="primary"
                        className="w-full"
                        onClick={() =>
                          navigate(
                            RouteNamesAuth.SIMULATOR.replace(':slug', slug!),
                          )
                        }
                      >
                        Simular
                      </Button>
                    </Card>
                  )}

                  {!product?.hasSimulation && (
                    <Card className="w-full max-w-full p-4 md:p-6">
                      <h3 className="text-lg uppercase font-light text-metal-900 pb-4 tracking-tighter">
                        Pedido de cotação
                      </h3>
                      <form onSubmit={formik.handleSubmit}>
                        <fieldset className="space-y-4">
                          <div className="space-y-2">
                            <Label>NIF</Label>
                            <Input
                              type="text"
                              name="nif"
                              placeholder="NIF Tomador"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.nif ?? ''}
                              className={
                                formik.errors.nif ? 'border-primary-500' : ''
                              }
                            />
                            {formik.errors.nif && formik.touched.nif && (
                              <span className="text-primary-500 text-body-5">
                                {formik.errors.nif}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Nome</Label>
                            <Input
                              type="text"
                              name="name"
                              placeholder="Nome Cliente"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.name ?? ''}
                              className={
                                formik.errors.name ? 'border-primary-500' : ''
                              }
                            />
                            {formik.errors.name && formik.touched.name && (
                              <span className="text-primary-500 text-body-5">
                                {formik.errors.name}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Morada</Label>
                            <Input
                              type="text"
                              name="address1"
                              placeholder="Morada"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.address1 ?? ''}
                              className={
                                formik.errors.address1 &&
                                formik.touched.address1
                                  ? 'border-primary-500'
                                  : ''
                              }
                            />
                            {formik.errors.address1 &&
                              formik.touched.address1 && (
                                <span className="text-primary-500 text-body-5">
                                  {formik.errors.address1}
                                </span>
                              )}
                          </div>
                          <div className="space-y-2">
                            <Label>Código Postal</Label>
                            <Input
                              type="text"
                              name="postal_code"
                              placeholder="####-###"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.postal_code ?? ''}
                              className={
                                formik.errors.postal_code &&
                                formik.touched.postal_code
                                  ? 'border-primary-500'
                                  : ''
                              }
                            />
                            {formik.errors.postal_code &&
                              formik.touched.postal_code && (
                                <span className="text-primary-500 text-body-5">
                                  {formik.errors.postal_code}
                                </span>
                              )}
                          </div>
                          <div className="space-y-2">
                            <Label>Mensagem</Label>
                            <Textarea
                              name="content"
                              placeholder="Dados para o pedido de cotação"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.content ?? ''}
                              rows={4}
                              className={
                                formik.errors.content && formik.touched.content
                                  ? 'border-primary-500'
                                  : ''
                              }
                            ></Textarea>
                            {formik.errors.content &&
                              formik.touched.content && (
                                <span className="text-primary-500 text-body-5">
                                  {formik.errors.content}
                                </span>
                              )}
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="submit"
                              disabled={isCallingApi}
                              onClick={() => formik.handleSubmit()}
                            >
                              Enviar
                            </Button>
                          </div>
                        </fieldset>
                      </form>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Wrapper>
    </div>
  )
}
